import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, getMyOrders } from '../api';
import axios from 'axios';

const s = {
    h1:    { fontSize: 22, fontWeight: 700, marginBottom: 20, color: '#1a1d23' },
    grid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 },
    card:  { background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    cLabel:{ fontSize: 12, color: '#6b7280', marginBottom: 4 },
    cVal:  { fontSize: 26, fontWeight: 700, color: '#1a1d23' },
    cSub:  { fontSize: 12, color: '#9ca3af', marginTop: 2 },
    sect:  { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
    secH:  { fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#1a1d23' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:    { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #f3f4f6', color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: .5 },
    td:    { padding: '10px 12px', borderBottom: '1px solid #f9fafb' },
    badge: (status) => ({
        display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
        background: status === 'completed' ? '#d1fae5' : status === 'processing' ? '#dbeafe' : status === 'pending' ? '#fef3c7' : status === 'error' ? '#fee2e2' : '#f3f4f6',
        color: status === 'completed' ? '#065f46' : status === 'processing' ? '#1d4ed8' : status === 'pending' ? '#92400e' : status === 'error' ? '#991b1b' : '#374151',
    }),
    btn:   { padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

// Map task_types to display-friendly names + icons
const SPEED_DISPLAY = [
    { key: 'like_post',                    icon: '❤️', label: 'Likes' },
    { key: 'follow_user',                  icon: '👥', label: 'Follows' },
    { key: 'watch_user_stories',           icon: '👁️', label: 'Story Views' },
    { key: 'watch_specific_reel',          icon: '🎬', label: 'Reel Views' },
    { key: 'comment_post',                 icon: '💬', label: 'Comments' },
    { key: 'save_post',                    icon: '📌', label: 'Saves' },
];

export default function Dashboard() {
    const [me, setMe] = useState(null);
    const [orders, setOrders] = useState([]);
    const [speeds, setSpeeds] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Always fetch OWN orders — admin sees all orders in Admin > All Orders
        const ordersPromise = getMyOrders({ limit: 10 }).catch(() => ({ data: [] }));

        const speedsPromise = axios.get('/public/delivery-stats').catch(() => ({ data: null }));

        Promise.all([getMe(), ordersPromise, speedsPromise]).then(([meRes, ordersRes, speedsRes]) => {
            setMe(meRes.data);
            const client = JSON.parse(localStorage.getItem('client') || '{}');
            localStorage.setItem('client', JSON.stringify({ ...client, balance: meRes.data.balance }));
            setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
            if (speedsRes.data?.stats) setSpeeds(speedsRes.data.stats);
        }).catch(console.error).finally(() => setLoading(false));
    }, []);

    if (loading) return <div>Loading...</div>;

    const stats = [
        { label: 'Balance',       val: `€${parseFloat(me?.balance || 0).toFixed(2)}`, sub: 'Available' },
        { label: 'Total Orders',  val: orders.length, sub: 'All time' },
        { label: 'Processing',    val: orders.filter(o => o.status === 'processing').length, sub: 'Active' },
        { label: 'Completed',     val: orders.filter(o => o.status === 'completed').length, sub: 'Done' },
    ];

    return (
        <div>
            <div className="og-dash-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <h1 style={{ ...s.h1, marginBottom: 0 }}>Dashboard</h1>
                <button style={s.btn} onClick={() => navigate('/new-order')}>+ New Order</button>
            </div>

            <div style={s.grid}>
                {stats.map(st => (
                    <div key={st.label} style={s.card}>
                        <div style={s.cLabel}>{st.label}</div>
                        <div style={s.cVal}>{st.val}</div>
                        <div style={s.cSub}>{st.sub}</div>
                    </div>
                ))}
            </div>

            {/* ── TIER STATUS CARD ── */}
            {me?.is_reseller && (() => {
                const tierColor = me.tier === 'elite' ? '#f59e0b' : me.tier === 'partner' ? '#a78bfa' : '#34d399';
                const tierBg = me.tier === 'elite' ? '#f59e0b' : me.tier === 'partner' ? '#8b5cf6' : '#10b981';
                const vol = parseFloat(me.monthly_volume || 0);
                const pct = Math.min(100, (vol / 500) * 100);
                const remaining = Math.max(0, 500 - vol);

                return (
                <div className="og-tier-card" style={{
                    marginBottom: 20, borderRadius: 14, overflow: 'hidden',
                    background: '#fff',
                    boxShadow: `0 2px 16px ${tierColor}15, 0 1px 4px rgba(0,0,0,0.06)`,
                    border: `1px solid ${tierColor}30`,
                    position: 'relative',
                }}>
                    {/* Top colored accent bar */}
                    <div style={{ height: 4, background: `linear-gradient(90deg, ${tierBg}, ${tierColor})` }} />

                    <div style={{ padding: '20px 24px' }}>
                        {/* Row 1: Tier name + discount badge + CTA */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 10,
                                    background: `${tierColor}15`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 20,
                                }}>{me.tier === 'elite' ? '⚫' : me.tier === 'partner' ? '🟣' : '🟢'}</div>
                                <div>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1d23', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        {me.tier || 'Reseller'} Tier
                                    </div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                                        {me.tier === 'elite' ? 'Reviewed weekly'
                                            : me.tier === 'partner' ? (me.tier_locked_until && new Date(me.tier_locked_until) > new Date()
                                                ? `Early Partner until ${new Date(me.tier_locked_until).toLocaleDateString()}`
                                                : 'Active')
                                            : 'Standard'}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                    padding: '6px 14px', borderRadius: 8, fontSize: 15, fontWeight: 800,
                                    background: `${tierColor}15`, color: tierColor,
                                }}>−{me.tier === 'elite' ? (me.elite_discount || 55) : me.discount_pct}% OFF</span>
                                <button onClick={() => navigate('/partner')} style={{
                                    padding: '8px 14px', borderRadius: 8, border: `1px solid ${tierColor}40`,
                                    background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    color: tierColor,
                                }}>
                                    View Program →
                                </button>
                            </div>
                        </div>

                        {/* Row 2: Progress / Status */}
                        {me.tier === 'reseller' ? (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                                    <span>Monthly volume</span>
                                    <span style={{ fontWeight: 700, color: pct >= 80 ? '#10b981' : '#374151' }}>€{vol.toFixed(0)} / €500</span>
                                </div>
                                <div style={{ height: 10, background: '#f3f4f6', borderRadius: 100, overflow: 'hidden' }}>
                                    <div style={{
                                        width: `${pct}%`, height: '100%',
                                        background: pct >= 80 ? `linear-gradient(90deg, #10b981, #34d399)` : `linear-gradient(90deg, #6366f1, #818cf8)`,
                                        borderRadius: 100, transition: 'width 0.5s',
                                    }} />
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                                    <span style={{ fontSize: 11, color: '#9ca3af' }}>
                                        {remaining > 0 ? `€${remaining.toFixed(0)} more to unlock Partner 🟣` : 'Almost there!'}
                                    </span>
                                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{pct.toFixed(0)}%</span>
                                </div>
                            </div>
                        ) : me.tier === 'partner' ? (
                            <div style={{
                                padding: '10px 14px', borderRadius: 8,
                                background: me.months_below_threshold > 0 ? '#fef3c7' : '#f0fdf4',
                                fontSize: 13, color: me.months_below_threshold > 0 ? '#92400e' : '#065f46',
                                display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                {me.months_below_threshold > 0
                                    ? `⚠️ Volume below €500 this month (${me.months_below_threshold}/2 grace months)`
                                    : '✅ Partner tier maintained — volume on track'}
                                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>€{vol.toFixed(0)}/mo</span>
                            </div>
                        ) : me.tier === 'elite' ? (
                            <div style={{
                                padding: '10px 14px', borderRadius: 8, background: '#fffbeb',
                                fontSize: 13, color: '#78350f',
                                display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                ⚫ Elite tier — personalized discount reviewed weekly based on your volume
                                <span style={{ marginLeft: 'auto', fontWeight: 700 }}>€{vol.toFixed(0)}/mo</span>
                            </div>
                        ) : null}
                    </div>
                </div>
                );
            })()}

            {/* Telegram Bot — styled like site */}
            <div style={{
                marginBottom: 20, borderRadius: 12, overflow: 'hidden',
                background: '#0b1121', border: '1px solid rgba(29,78,216,0.25)',
                padding: '22px 26px', color: '#f1f5f9',
                boxShadow: '0 2px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: '50%',
                                background: 'linear-gradient(135deg, #1d4ed8, #06b6d4)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                            }}>💬</div>
                            <div>
                                <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: '-0.3px' }}>Telegram Bot</div>
                                <div style={{ fontSize: 11, color: '#06b6d4', fontWeight: 600 }}>Notifications & Live Support</div>
                            </div>
                        </div>
                        <div className="og-tg-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                            {[
                                { icon: '📬', title: 'Order updates', desc: 'Instant notification on completion' },
                                { icon: '📊', title: 'Daily recap', desc: 'Subscription delivery summary' },
                                { icon: '💬', title: 'Live support', desc: 'Chat with our team anytime' },
                            ].map(f => (
                                <div key={f.title} style={{
                                    background: 'rgba(255,255,255,0.04)', borderRadius: 8, padding: '10px 12px',
                                    border: '1px solid rgba(255,255,255,0.06)'
                                }}>
                                    <div style={{ fontSize: 16, marginBottom: 4 }}>{f.icon}</div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', marginBottom: 2 }}>{f.title}</div>
                                    <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>{f.desc}</div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <a href={`https://t.me/orbitgrowth_bot?start=link_${me?.api_key || ''}`} target="_blank" rel="noopener noreferrer"
                               style={{
                                   padding: '9px 22px',
                                   background: 'linear-gradient(135deg, #1d4ed8, #06b6d4)',
                                   color: '#fff', border: 'none', borderRadius: 8,
                                   fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap',
                               }}>
                                Connect in 1 click
                            </a>
                            <a href="https://t.me/providerinstagram" target="_blank" rel="noopener noreferrer"
                               style={{
                                   padding: '9px 22px',
                                   background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                                   color: '#e2e8f0', borderRadius: 8,
                                   fontSize: 13, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap',
                               }}>
                                📡 News Channel
                            </a>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 6, padding: '5px 10px', border: '1px solid rgba(255,255,255,0.08)' }}>
                                <code style={{ fontSize: 11, color: '#94a3b8' }}>/link {me?.api_key || 'YOUR_API_KEY'}</code>
                                <button
                                    onClick={() => { navigator.clipboard.writeText(`/link ${me?.api_key || ''}`); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#64748b', padding: '0 2px' }}
                                    title="Copy command"
                                >📋</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Delivery Rates — compact inline strip */}
            {speeds && (
                <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>Platform delivery rates (avg/h, last 7d):</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {SPEED_DISPLAY.map(sd => {
                            const data = speeds[sd.key];
                            if (!data) return null;
                            return (
                                <span key={sd.key} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    background: '#f8fafc', border: '1px solid #f1f5f9',
                                    borderRadius: 6, padding: '4px 10px', fontSize: 12,
                                }}>
                                    <span style={{ fontSize: 13 }}>{sd.icon}</span>
                                    <span style={{ fontWeight: 600, color: '#4f46e5' }}>~{data.avg_display}/h</span>
                                    <span style={{ color: '#9ca3af', fontSize: 11 }}>{sd.label}</span>
                                </span>
                            );
                        })}
                    </div>
                </div>
            )}

            <div style={s.sect}>
                <div style={s.secH}>Recent Orders</div>
                {orders.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: 14 }}>No orders yet. <span style={{ color: '#6366f1', cursor: 'pointer' }} onClick={() => navigate('/new-order')}>Place your first order →</span></p>
                ) : (
                    <div className="og-table-wrap">
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <th style={s.th}>#</th>
                                <th style={s.th}>Service</th>
                                <th style={s.th}>Link</th>
                                <th style={s.th}>Qty</th>
                                <th style={s.th}>Status</th>
                                <th style={s.th}>Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.slice(0, 10).map(o => (
                                <tr key={o.id}>
                                    <td style={s.td}>{o.id}</td>
                                    <td style={s.td}>{o.service_name || o.service_id}</td>
                                    <td style={{ ...s.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.link}</td>
                                    <td style={s.td}>{o.quantity}</td>
                                    <td style={s.td}><span style={s.badge(o.status)}>{o.status}</span></td>
                                    <td style={s.td}>€{parseFloat(o.price).toFixed(4)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </div>
        </div>
    );
}
